import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { IGetInputsRequestParams } from "Dashboard/scenes/Consumables/domain/IGetInputsRequestParams";
import { useAppDispatch } from "hooks/hooks";
import {
  Control,
  Controller,
  UseFormGetValues,
  UseFormHandleSubmit,
  UseFormRegister,
  UseFormSetFocus,
  UseFormSetValue
} from "react-hook-form";
import CategorySelect from "./CategorySelect";
import { setSearchInputPopover } from "../input.slice";
import DatePicker from "react-datepicker";
import { FormatDateIso } from "utils/dateUtils";
import { SearchType } from "Dashboard/domain/SearchType";
import classes from "../../../../../../components/Popover/popover.module.scss";
import classesConsumables from "../../../popover.module.scss";
import { styles } from "../../../../../../components/Popover/popoverSelectStyle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLongArrowAltDown } from "@fortawesome/free-solid-svg-icons";
import PartnerSelectForSearch from "Dashboard/scenes/Partners/components/PartnerSelectForSearch";
import UnitSelect from "Dashboard/components/UnitSelect";

interface IProps {
  handleSubmit: UseFormHandleSubmit<IGetInputsRequestParams>;
  register: UseFormRegister<IGetInputsRequestParams>;
  control: Control<IGetInputsRequestParams, any>;
  getValues: UseFormGetValues<IGetInputsRequestParams>;
  reset: () => void;
  setFocus: UseFormSetFocus<IGetInputsRequestParams>
  searchTypeValue: SearchType;
  setSearchTypeValue: React.Dispatch<React.SetStateAction<SearchType>>;
  setValue: UseFormSetValue<IGetInputsRequestParams>;
  setGetInputsRequestParams: Dispatch<SetStateAction<IGetInputsRequestParams>>;
}

const SearchInputModal = (props: IProps) => {
  const dispatch = useAppDispatch();

  const {
    register,
    handleSubmit,
    control,
    getValues,
    setFocus,
    setValue
  } = props;

  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>(
    [getValues("dateFrom") ? new Date(getValues("dateFrom")!) : null,
    getValues("dateTo") ? new Date(getValues("dateTo")!) : null]);

  const close = () => {
    dispatch(setSearchInputPopover({ isOpen: false }));
  };

  const onSubmit = async (data: IGetInputsRequestParams) => {
    try {
      if (props.searchTypeValue === SearchType.BASIC) {
        data = { searchTerm: data.searchTerm };
      } else {
        data.searchTerm = undefined;
      }
      props.setGetInputsRequestParams(data);
      close();
    } catch (err) {
      console.error(err);
    }
  };

  const switchSearchMode = (searchType: SearchType) => {
    props.setSearchTypeValue(searchType);
  };

  useEffect(() => {
    if (props.searchTypeValue === SearchType.BASIC) {
      setFocus("searchTerm");
    } else {
      setFocus("code");
    }
  }, [setFocus, props.searchTypeValue]);

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className={`
        ${classesConsumables.searchInputPopover}
        ${classes.basicPopover}`}
    >
      {props.searchTypeValue === SearchType.BASIC && (
        <>
          <div className={classes.row}>
            <div className={`${classes.col} ${classes.colDir}`}>
              <input type="text"
                className={`${classes.fullWidth} ${classes.itemBorder}`}
                {...register("searchTerm")}
              />
            </div>
            <div className={classes.gPopoverDisclaimer}>
              * možete uneti više reči tj. kriterijuma pretrage
            </div>
          </div>

          <div className={classes.row}>
            <div className={classes.buttons}>
              <label
                onClick={() => switchSearchMode(SearchType.ADVANCED)}
                className={classes.clickableText}
              >
                NAPREDNA PRETRAGA
              </label>
              <button
                className={classes.iconButton}
                type="submit"
              >
                <FontAwesomeIcon
                  icon={faLongArrowAltDown}
                  className={classes.icon} />
              </button>
            </div>
          </div>
        </>
      )}
      {props.searchTypeValue === SearchType.ADVANCED && (
        <>
          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>šifra artikla</label>
              <input type="text" className={classes.itemBorder}
                {...register("code", { required: false })} />
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>br. otpremnice</label>
              <input type="text" className={classes.itemBorder}
                {...register("billOfLandingId", { required: false })} />
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>ime</label>
              <input type="text" className={classes.itemBorder}
                {...register("name")} />
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>dobavljač</label>
              <Controller
                name="supplier"
                control={control}
                defaultValue={undefined}
                rules={{
                  required: false,
                }}
                render={({ field: { onChange } }) => (
                  <PartnerSelectForSearch
                    value={getValues("supplier")}
                    onChange={onChange}
                    customStyles={styles.customStyleClassic}
                  />
                )}
              />
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>kategorija</label>
              <Controller
                name="categories"
                control={control}
                defaultValue={undefined}
                rules={{
                  required: false,
                }}
                render={({ field: { onChange } }) => (
                  <CategorySelect
                    value={getValues("categories")}
                    onChange={onChange}
                    customStyles={styles.customSolidBorder}
                  />
                )}
              />
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>količina</label>
              <input type="text" className={classes.itemBorder}
                {...register("quantityAmountTotalRange", { required: false })} />
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>mera</label>
              <Controller
                name="quantityUnit"
                control={control}
                defaultValue={undefined}
                rules={{
                  required: false,
                }}
                render={({ field: { onChange } }) => (
                  <UnitSelect
                    value={getValues("quantityUnit")}
                    onChange={onChange}
                    customStyles={styles.customStyleClassic}
                  />
                )}
              />
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>cena po kom.</label>
              <input type="text" className={classes.itemBorder}
                {...register("pricePerUnitRange", { required: false })} />
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>kol. ostalo</label>
              <input type="text" className={classes.itemBorder}
                {...register("quantityAmountRemainingRange", { required: false })} />
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>ukupno</label>
              <input type="text" className={classes.itemBorder}
                {...register("totalValueRange", { required: false })} />
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>datum ulaza</label>
              <div className="datepickerWrapper">
                <DatePicker
                  selectsRange={true}
                  startDate={dateRange[0]}
                  endDate={dateRange[1]}
                  showMonthDropdown
                  showYearDropdown
                  onChange={(date: any) => {
                    setDateRange([date[0], date[1]]);
                    setValue("dateFrom", date[0] && FormatDateIso(date[0]));
                    setValue("dateTo", date[1] && FormatDateIso(date[1]));
                  }}
                  isClearable={true}
                  className={`${classes.itemBorder} ${classes.dateItem}`}
                />
              </div>
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>napomena</label>
              <input type="text" className={classes.itemBorder}
                {...register("note", { required: false })} />
            </div>
          </div>

          <div className={classes.row}>
            <div className={classes.buttons}>
              <label
                onClick={() => switchSearchMode(SearchType.BASIC)}
                className={classes.clickableText}
              >
                PAMETNA PRETRAGA
              </label>
              <button className={classes.iconButton} type="submit">
                <FontAwesomeIcon
                  icon={faLongArrowAltDown}
                  className={classes.icon} />
              </button>
            </div>
          </div>
        </>
      )}
    </form>
  );
};

export default SearchInputModal;
